import React from 'react';
import { ArrowRight } from 'lucide-react';
import { capabilities, SAAS_SIGNUP_URL } from './saasContent';

const funnelStages = ['Capture leads', 'Convert', 'Follow up', 'Book meetings', 'Keep paying'];

const SaasCapabilitiesSection = () => {
  return (
    <section
      id="tabbed-features"
      className="py-16 md:py-28 bg-white overflow-x-hidden border-y border-stone-100/80"
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-stone-600 mb-3 md:mb-4">
            What you get
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-stone-900 leading-[1.12]">
            Your entire AI growth engine{' '}
            <span className="bg-gradient-to-r from-[#6ee7a8] via-[#8ae690] to-[#5ec269] bg-clip-text text-transparent">
              in one place
            </span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-stone-600 leading-relaxed max-w-2xl mx-auto">
            Capture leads, turn them into customers, follow up, book meetings, and keep people paying. Without juggling
            five different tools.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6 sm:gap-7 lg:gap-8">
          {capabilities.map(({ Icon, title, description, tags, accent }, index) => (
            <article
              key={title}
              className={`group relative flex flex-col gap-5 rounded-3xl border p-7 sm:p-8 min-h-[17.5rem] sm:min-h-[18.5rem] shadow-[0_22px_60px_-28px_rgba(15,23,42,0.14)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_28px_72px_-32px_rgba(15,23,42,0.2)] ${accent.card} ${
                index < 3 ? 'lg:col-span-2' : 'lg:col-span-3'
              }`}
            >
              <div
                className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] ring-1 sm:h-[4.5rem] sm:w-[4.5rem] ${accent.icon}`}
                aria-hidden
              >
                <Icon
                  size={40}
                  weight="duotone"
                  className={`sm:w-11 sm:h-11 ${accent.iconColor}`}
                  aria-hidden
                />
              </div>
              <div className="min-w-0 flex-1 flex flex-col">
                <h3 className="text-xl sm:text-2xl font-semibold text-stone-900 tracking-tight">{title}</h3>
                <p className="mt-3 text-base sm:text-lg text-stone-600 leading-relaxed flex-1">{description}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${accent.tag}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-8 md:mt-10 rounded-3xl border border-stone-200/80 bg-gradient-to-br from-stone-50 via-white to-slate-50/60 p-6 sm:p-8 shadow-[0_22px_60px_-28px_rgba(15,23,42,0.12)]">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-sm sm:text-base text-stone-700 font-medium">
            {funnelStages.map((stage, i) => (
              <React.Fragment key={stage}>
                <span className="rounded-full border border-stone-200 bg-white px-4 py-2 shadow-sm">{stage}</span>
                {i < funnelStages.length - 1 && (
                  <ArrowRight className="h-4 w-4 text-stone-400 shrink-0" aria-hidden />
                )}
              </React.Fragment>
            ))}
          </div>
          <p className="mt-5 text-center text-sm sm:text-base text-stone-600 max-w-xl mx-auto leading-relaxed">
            Kolanut connects your stack and shows you what to do next. You approve it. Run it from web or phone.
          </p>
          <div className="mt-6 flex justify-center">
            <a
              href={SAAS_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 h-12 md:h-14 px-8 md:px-10 rounded-full bg-[#8ae690] text-black font-semibold text-sm md:text-base shadow-md hover:bg-[#7ad67f] hover:shadow-lg transition-all duration-200"
            >
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/70">
                <img src="/kolanut_logo.png" alt="Kolanut logo" className="w-4 h-4 object-contain" />
              </span>
              Try Kolanut now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SaasCapabilitiesSection;
